/**
 * Company detail — the company, its parent legal entity, and the branches
 * and departments beneath it.
 *
 * Arabic and English (C7): every word from the catalogs; the tabs are chosen
 * by id, never by their words.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { ltr } from "@/lib/i18n/format";
import { formatDate } from "@/lib/money";
import { addressLine } from "./types";
import type {
  BranchRow,
  CompanyRow,
  DepartmentRow,
  LegalEntityRow,
} from "./types";

export function CompanyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const [tab, setTab] = useState<string>("overview");

  const company = useApiQuery<CompanyRow>(
    ["org", "company", id],
    `/companies/${id}`,
    { enabled: Boolean(id) },
  );
  const entities = useApiList<LegalEntityRow>(
    ["org", "legal-entities"],
    "/legal-entities",
  );
  const branches = useApiList<BranchRow>(["org", "branches"], "/branches");
  const departments = useApiList<DepartmentRow>(
    ["org", "departments"],
    "/departments",
  );

  if (company.isLoading) return <Skeleton rows={10} />;
  if (company.error || !company.data) {
    return (
      <EmptyState
        title={t("org.company.notFound")}
        message={company.error ? undefined : t("org.legalEntity.archived")}
        action={
          company.error ? (
            <div role="alert" style={{ fontSize: 14 }}>
              <ErrorText error={company.error} />
            </div>
          ) : undefined
        }
      />
    );
  }

  const row = company.data;
  const entity = entities.data?.items.find((e) => e.id === row.legal_entity_id);
  const ownBranches = (branches.data?.items ?? []).filter(
    (b) => b.company_id === row.id,
  );
  const ownDepartments = (departments.data?.items ?? []).filter(
    (d) => d.company_id === row.id,
  );

  return (
    <div>
      <PageHeader
        title={row.name}
        subtitle={
          entity ? t("org.company.partOf", { name: entity.name }) : undefined
        }
        breadcrumb={[t("org.section"), t("org.companies.title"), row.name]}
      />

      <Tabs
        tabs={[
          { id: "overview", label: t("org.company.overview") },
          {
            id: "branches",
            label: t("org.company.branchesTab", { count: ownBranches.length }),
          },
          {
            id: "departments",
            label: t("org.company.departmentsTab", {
              count: ownDepartments.length,
            }),
          },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "overview" && (
        <Card title={t("org.company.details")} className="mt-4">
          <dl className="atlas-grid atlas-grid--3">
            <div>
              <dt className="atlas-type-label">{t("fields.code")}</dt>
              <dd className="atlas-mono">{row.code}</dd>
            </div>
            <div>
              <dt className="atlas-type-label">{t("fields.status")}</dt>
              <dd>
                <StatusBadge status={row.status} type="Company" />
              </dd>
            </div>
            <div>
              <dt className="atlas-type-label">{t("fields.legalEntity")}</dt>
              <dd>
                {entity ? (
                  <Link to={`/app/organization/legal-entities/${entity.id}`}>
                    {entity.name}
                  </Link>
                ) : (
                  // A company whose parent is not visible cannot post, so this
                  // says so rather than showing a bare dash.
                  <span style={{ color: "var(--atlas-danger)" }}>
                    {t("org.company.parentMissing")}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="atlas-type-label">{t("fields.currency")}</dt>
              <dd className="atlas-mono">{entity?.currency_code ?? "—"}</dd>
            </div>
            <div>
              <dt className="atlas-type-label">{t("fields.created")}</dt>
              <dd>{formatDate(row.created_at)}</dd>
            </div>
          </dl>
        </Card>
      )}

      {tab === "branches" && (
        <Card title={t("org.branches.title")} className="mt-4">
          {ownBranches.length === 0 ? (
            <EmptyState
              title={t("org.branches.emptyTitle")}
              message={t("org.company.singleSite")}
            />
          ) : (
            <DataTable<BranchRow>
              rows={ownBranches}
              columns={[
                { key: "name", label: t("fields.name") },
                {
                  key: "code",
                  label: t("fields.code"),
                  render: (b) => <span className="atlas-mono">{b.code}</span>,
                },
                {
                  key: "timezone",
                  label: t("fields.timezone"),
                  render: (b) => ltr(b.timezone ?? t("shared.utc")),
                },
                {
                  key: "address",
                  label: t("fields.address"),
                  render: (b) => addressLine(b.address, i18n.language),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (b) => (
                    <StatusBadge status={b.status} type="Branch" />
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "departments" && (
        <Card title={t("org.departments.title")} className="mt-4">
          {ownDepartments.length === 0 ? (
            <EmptyState
              title={t("org.departments.emptyTitle")}
              message={t("org.company.departmentsHint")}
            />
          ) : (
            <DataTable<DepartmentRow>
              rows={ownDepartments}
              columns={[
                { key: "name", label: t("fields.name") },
                {
                  key: "code",
                  label: t("fields.code"),
                  render: (d) => <span className="atlas-mono">{d.code}</span>,
                },
                {
                  key: "parent_department_id",
                  label: t("org.departments.reportsTo"),
                  render: (d) =>
                    d.parent_department_id
                      ? (ownDepartments.find(
                          (x) => x.id === d.parent_department_id,
                        )?.name ?? "—")
                      : t("org.departments.topLevel"),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
